import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Users, Clock, Award, CheckCircle2 } from 'lucide-react';

const features = [
  {
    icon: Shield,
    title: 'Certified Technicians',
    description: 'Our ASE-certified mechanics bring years of expertise to every service.',
  },
  {
    icon: Clock,
    title: 'Fast Turnaround',
    description: 'Most services completed same-day so you can get back on the road.',
  },
  {
    icon: Award,
    title: 'Quality Guarantee',
    description: '12-month warranty on all parts and labor for your peace of mind.',
  },
  {
    icon: Users,
    title: 'Transparent Pricing',
    description: 'No hidden fees. Get upfront quotes before any work begins.',
  },
];

const checkpoints = [
  'Latest diagnostic equipment',
  'Genuine OEM parts available',
  'Free multi-point inspections',
  'Digital service reports',
  'Comfortable waiting area',
  'Shuttle service available',
];

export default function WhyChooseUs() {
  return (
    <section className="py-24 bg-slate-900 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent opacity-30" />
      <div className="absolute -top-40 -right-40 w-80 h-80 bg-amber-500/5 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-amber-500/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-1.5 bg-amber-500/10 text-amber-400 text-sm font-semibold rounded-full mb-4">
              Why Choose Us
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Excellence in Every Detail
            </h2>
            <p className="text-lg text-slate-400 mb-10">
              We combine cutting-edge technology with old-fashioned customer service to deliver an 
              automotive care experience that exceeds expectations.
            </p>

            {/* Checkpoints */}
            <div className="grid grid-cols-2 gap-4">
              {checkpoints.map((point, i) => (
                <motion.div
                  key={point}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <CheckCircle2 className="w-5 h-5 text-amber-400 flex-shrink-0" />
                  <span className="text-slate-300 text-sm">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Features */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="p-6 bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 hover:border-amber-500/30 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-slate-900" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-400 text-sm">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}